import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { CardComponent } from '../../../core/panels/card.component';
import { ChartConfig } from '../../../core/chart/chart.component';
import { SidekickChartComponent } from './sidekick-chart.component';
import { SidekickSummaryDetail } from '../../../../models/sidekick-summary.model';
import { BaseComponent } from '../../../base/base.component';

@Component({
  selector: 'app-gl-summary',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    CardComponent,
    SidekickChartComponent,
  ],
  template: ` <div class="tw-flex tw-gap-4">
    <div [class]="showFinancials ? 'tw-basis-2/3' : 'tw-basis-full'">
      <div class="tw-flex tw-gap-8 tw-pb-8">
        <div class="tw-basis-full">
          <app-detail-panel [dataSource]="dataSource">
            <app-detail-item
              label="Type of injury/damages"
              name="typeOfInjury"
              labelPosition="Top"
              labelCase="none"
            />
            <app-detail-item
              label="Liability assessment"
              name="liabilityAssessment"
              labelPosition="Top"
            />
            <app-detail-item
              label="Claim status"
              name="claimStatus"
              labelPosition="Top"
            />
            <app-detail-item
              label="Summary of actions"
              name="summaryOfActions"
              labelPosition="Top"
            />
            <app-detail-item
              label="Legal aspects"
              name="legalAspects"
              labelPosition="Top"
            />
            <app-detail-item
              label="Path to resolution"
              name="pathToResolution"
              labelPosition="Top"
            />
          </app-detail-panel>
        </div>
      </div>
      <div class="tw-flex tw-gap-8 tw-pb-8">
        <div class="tw-basis-full">
          <app-card label="Claim Details">
            <div class="tw-flex tw-gap-8 tw-pb-4">
              <div class="tw-basis-1/2">
                <app-detail-panel [dataSource]="dataSource">
                  <app-detail-item label="Claimant" name="claimant" />
                  @if (showClaimantDateOfBirth) {
                    <app-detail-item
                      label="Claimant date of birth"
                      name="claimantDateOfBirth"
                      format="d"
                    />
                  }
                  @if (showCurrentAge) {
                    <app-detail-item label="Current Age" name="currentAge" />
                  }
                  <app-detail-item
                    label="Client Location"
                    name="clientLocation"
                  />
                  <app-detail-item label="Client Name" name="clientName" />
                  @if (showAccountName) {
                    <app-detail-item label="Account Name" name="accountName" />
                  }
                  <app-detail-item label="Claim Type" name="claimType" />
                  <app-detail-item label="Loss Location" name="lossLocation" />
                </app-detail-panel>
              </div>
              <div class="tw-basis-1/2">
                <app-detail-panel [dataSource]="dataSource">
                  <app-detail-item
                    label="Date Prepared"
                    name="datePrepared"
                    format="d"
                  />
                  <app-detail-item
                    label="Date Of Loss"
                    name="dateOfLoss"
                    format="d"
                  />
                  <app-detail-item
                    label="Claims Examiner"
                    name="claimsExaminer"
                  />
                  <app-detail-item label="Claim Number" name="claimNumber" />
                  @if (showDateClaimReported) {
                    <app-detail-item
                      label="Date Claim Reported"
                      name="dateClaimReported"
                      format="d"
                    />
                  }
                  @if (showDateClosed) {
                    <app-detail-item
                      label="Date of Claim Closure"
                      name="dateClosed"
                      format="d"
                    />
                  }
                </app-detail-panel>
              </div>
            </div>
            <div>
              <hr />
            </div>
            <div class="tw-flex tw-gap-8 tw-pt-4">
              <div class="tw-basis-1/2">
                <app-detail-panel [dataSource]="dataSource">
                  <app-detail-item label="Subrogation" name="subrogation" />
                  <app-detail-item label="Litigation" name="litigation" />
                  <app-detail-item
                    label="Claimant's Attorney"
                    name="claimantsAttorney"
                  />
                  <app-detail-item
                    label="Punitive Damages"
                    name="punitiveDamages"
                  />
                </app-detail-panel>
              </div>
              <div class="tw-basis-1/2">
                <app-detail-panel [dataSource]="dataSource">
                  <app-detail-item label="Excess" name="excess" />
                  <app-detail-item label="Court" name="court" />
                  <app-detail-item
                    label="Defense Attorney"
                    name="defenseAttorney"
                  />
                  <app-detail-item label="Tendered" name="tendered" />
                </app-detail-panel>
              </div>
            </div>
          </app-card>
        </div>
      </div>
    </div>
    @if (showFinancials) {
      <div class="tw-basis-1/3">
        <div class="tw-flex tw-pl-8 tw-flex-col tw-pb-4">
          <app-sidekick-chart
            [dataSource]="indemnityDataSource"
            title="Loss"
          />
          <app-sidekick-chart
            [dataSource]="expenseDataSource"
            title="Expense"
          />
          <app-sidekick-chart [dataSource]="totalsDataSource" />
        </div>
      </div>
    }
  </div>`,
  styles: [],
})
export class GlSummaryComponent extends BaseComponent implements OnInit {
  @ViewChildren(SidekickChartComponent) private chartComponents!: QueryList<SidekickChartComponent>;

  showCurrentAge = true;
  showClaimantDateOfBirth = true;
  showAccountName = true;
  showDateClaimReported = true;
  showDateClosed = true;
  showFinancials = true;
  private _dataSource: SidekickSummaryDetail | null = null;

  @Input()
  set dataSource(value: SidekickSummaryDetail | null) {
    this._dataSource = value;
    if (value) {
      this.initializeCharts();
      this.view.hideLoading();
    }
  }

  get dataSource(): SidekickSummaryDetail | null {
    return this._dataSource;
  }

  expenseDataSource: any = {
    firstPartyDeductible: 0,
    totalIncurred: 0,
    configData: new ChartConfig(),
  };
  totalsDataSource: any = {
    firstPartyDeductible: 0,
    totalIncurred: 0,
    configData: new ChartConfig(),
  };
  indemnityDataSource: any = {
    firstPartyDeductible: 0,
    totalIncurred: 0,
    configData: new ChartConfig(),
  };

  ngOnInit(): void {
    this.initFieldSecurity();
    this.showFinancials = this.session.authorize('ClaimSummaryIncurred');

    if (this._dataSource) {
      this.initializeCharts();
    }
    this.view.hideLoading();
  }

  private initFieldSecurity(): void {
    const restricted = this.session.user?.fieldSecurity?.restrictedFields || [];
    this.showCurrentAge = !restricted.includes('CurrentAge');
    this.showClaimantDateOfBirth = !restricted.includes('DateOfBirth');
    this.showAccountName = !restricted.includes('AccountName');
    this.showDateClaimReported = !restricted.includes('DateClaimReported');
    this.showDateClosed = !restricted.includes('DateClosed');
  }

  private initializeCharts(): void {
    if (!this._dataSource?.financials) {
      return;
    }

    const paidColor = '#3b3cc2';
    const reserveColor = '#08857d';

    // Indemnity/Loss chart
    this.indemnityDataSource.configData = new ChartConfig();
    this.indemnityDataSource.configData.showLabels = false;
    this.indemnityDataSource.configData.addSeries(
      'Paid',
      this._dataSource.financials.indemnityPaid ?? 0,
      paidColor,
    );
    this.indemnityDataSource.configData.addSeries(
      'Reserve',
      this._dataSource.financials.indemnityReserve ?? 0,
      reserveColor,
    );
    this.indemnityDataSource.totalIncurred =
      this._dataSource.financials.indemnityIncurred ?? 0;
    this.indemnityDataSource.firstPartyDeductible =
      this._dataSource.financials.indemnityDeductible ?? 0;

    // Expense chart
    this.expenseDataSource.configData = new ChartConfig();
    this.expenseDataSource.configData.showLabels = false;
    this.expenseDataSource.configData.addSeries(
      'Paid',
      this._dataSource.financials.expenseOtherPaid ?? 0,
      paidColor,
    );
    this.expenseDataSource.configData.addSeries(
      'Reserve',
      this._dataSource.financials.expenseOtherReserve ?? 0,
      reserveColor,
    );
    this.expenseDataSource.totalIncurred =
      this._dataSource.financials.expenseOtherIncurred ?? 0;
    this.expenseDataSource.firstPartyDeductible =
      this._dataSource.financials.expenseDeductible ?? 0;

    // Totals chart
    this.totalsDataSource.configData = new ChartConfig();
    this.totalsDataSource.configData.showLabels = false;
    this.totalsDataSource.configData.addSeries(
      'Paid',
      this._dataSource.financials.totalPaid ?? 0,
      paidColor,
    );
    this.totalsDataSource.configData.addSeries(
      'Reserve',
      this._dataSource.financials.totalReserve ?? 0,
      reserveColor,
    );
    this.totalsDataSource.totalIncurred =
      this._dataSource.financials.totalIncurred ?? 0;
    this.totalsDataSource.firstPartyDeductible =
      this._dataSource.financials.totalDeductible ?? 0;
  }

  captureFinancialChartImages(): string[] {
    return this.chartComponents
      .map((chart) => chart.captureDonutImage())
      .filter((image): image is string => image != null && image.length > 0);
  }
}
